import { gql } from "@apollo/client";

export const LOGIN_USER = gql`
  mutation LoginUser($email: String!, $password: String!) {
    loginUser(email: $email, password: $password) {
      success
      message
      token
      user {
        id
        email
        fullname
        role
        created_at
        updated_at
      }
    }
  }
`;

// Student login path. Unlike loginUser (isActive-only), this allows a system
// auto-inactive student (autoInactive=true) to self-return, and blocks
// admin-deactivated / ARCHIVED / DELETED with the correct message. Mirrors the
// mobile apps: try loginStudent first, fall back to loginUser for admin/instructor.
export const LOGIN_STUDENT = gql`
  mutation LoginStudent($email: String!, $password: String!) {
    loginStudent(email: $email, password: $password) {
      success
      message
      token
      user {
        id
        email
        fullname
        role
        created_at
        updated_at
      }
    }
  }
`;

export const SIGNUP_USER = gql`
  mutation SignupUser($input: SignupInput!) {
    signupUser(input: $input) {
      success
      message
      user {
        id
        email
      }
    }
  }
`;

export const FORGOT_PASSWORD = gql`
  mutation ForgotPassword($email: String!) {
    forgotPassword(email: $email) {
      success
      message
    }
  }
`;

export const RESET_PASSWORD = gql`
  mutation ResetPassword($token: String!, $newPassword: String!) {
    resetPassword(token: $token, newPassword: $newPassword) {
      success
      message
    }
  }
`;

export const DOWNLOAD_PASS_INVOICE = gql`
  mutation DownloadPassInvoice($purchaseId: ID!) {
    downloadPassInvoice(purchaseId: $purchaseId) {
      success
      message
      filename
      mimeType
      contentBase64
    }
  }
`;

export const DOWNLOAD_INVOICE = gql`
  mutation DownloadInvoice($paymentId: ID!) {
    downloadInvoice(paymentId: $paymentId) {
      success
      message
      filename
      mimeType
      contentBase64
    }
  }
`;

export const CHANGE_PASSWORD = gql`
  mutation ChangePassword($newPassword: String!) {
    changePassword(newPassword: $newPassword) {
      success
      message
    }
  }
`;

export const UPDATE_PROFILE = gql`
mutation UpdateProfile($input: UpdateProfileInput!) {
  updateProfile(input: $input) {
    success
    message
    user {
      id
      email
      fullname
      phoneNumber
      additionalContact
      address
      country
      postcode
      uploadPhoto
      updated_at
    }
  }
}
`;

export const CREATE_ADMIN = gql`
  mutation CreateAdmin($input: CreateAdminInput!) {
    createAdmin(input: $input) {
      success
      message
    }
  }
`;
export const UPDATE_ADMIN = gql`
  mutation UpdateAdmin($userId: ID!, $input: UpdateAdminInput!) {
    updateAdmin(userId: $userId, input: $input) {
      success
      message
      data {
        id
        email
        role
        fullname
        phoneNumber
        country
        postcode
        address
      }
    }
  }
`;
export const DELETE_ADMIN = gql`
  mutation DeleteAdmin($userId: ID!) {
    deleteAdmin(userId: $userId) {
      success
      message
    }
  }
`;
export const UPDATE_APPROVAL = gql`
  mutation UpdateApproval($id: ID!, $approval: Boolean!) {
    updateApproval(id: $id, approval: $approval) {
      success
      message
      data {
        id
        isActive
        accountStatus
      }
    }
  }
`;

export const CREATE_CLASS = gql`
  mutation CreateClass($input: CreateClassInput!) {
    createClass(input: $input) {
      success
      message
      data {
        id
        className
        instructorName
        instructorId
        classType
        day
        startTime
        endTime
        enrolledStudents
        coverImage
        description
        created_at
        updated_at
      }
    }
  }
`;

export const UPDATE_CLASS = gql`
  mutation UpdateClass($id: ID!, $input: UpdateClassInput!) {
    updateClass(id: $id, input: $input) {
      success
      message
      data {
        id
        className
        instructorName
        instructorId
        classType
        day
        startTime
        endTime
        enrolledStudents
        coverImage
        description
        created_at
        updated_at
      }
    }
  }
`;

export const DELETE_CLASS = gql`
  mutation DeleteClass($id: ID!, $date: String) {
    deleteClass(id: $id, date: $date) {
      success
      message
    }
  }
`;
export const CREATE_INSTRUCTOR = gql`
  mutation CreateInstructor($input: CreateInstructorInput!) {
    createInstructor(input: $input) {
      success
      message
      data {
        id
        fullname
        email
        instructorId
      }
    }
  }
`;
export const UPDATE_INSTRUCTOR = gql`
  mutation UpdateInstructor($id: ID!, $input: UpdateInstructorInput!) {
    updateInstructor(id: $id, input: $input) {
      success
      message
      data {
        id
        fullname
      }
    }
  }
`;
export const DELETE_INSTRUCTOR = gql`
  mutation DeleteInstructor($id: ID!) {
    deleteInstructor(id: $id) {
      success
      message
      data {
        id
        fullname
      }
    }
  }
`;

export const CREATE_OR_UPDATE_ATTENDANCE = gql`
  mutation CreateOrUpdateAttendance($input: AttendanceInput!) {
    createOrUpdateAttendance(input: $input) {
      success
      message
      data {
        id
        date
        students {
          studentId
          studentName
          isPresent
        }
        presentCount
        absentCount
      }
    }
  }
`;

export const CREATE_STUDENT = gql`
  mutation CreateStudent($input: CreateStudentInput!) {
    createStudent(input: $input) {
      success
      message
      data {
        id
        studentId
        fullName
        isActive
      }
    }
  }
`;

export const UPDATE_STUDENT = gql`
  mutation UpdateStudent($id: ID!, $input: UpdateStudentInput!) {
    updateStudent(id: $id, input: $input) {
      success
      message
      data {
        id
        studentId
        fullName
        isActive
        accountStatus
      }
    }
  }
`;

export const DELETE_STUDENT = gql`
  mutation DeleteStudent($id: ID!, $justification: String!) {
    deleteStudent(id: $id, justification: $justification) {
      success
      message
      data {
        id
        accountStatus
        deletionPending
        deletionScheduledFor
      }
    }
  }
`;

export const RESTORE_STUDENT = gql`
  mutation RestoreStudent($id: ID!, $justification: String!) {
    restoreStudent(id: $id, justification: $justification) {
      success
      message
      data {
        id
        accountStatus
        isActive
      }
    }
  }
`;

export const DELETE_MY_ACCOUNT = gql`
  mutation DeleteMyAccount($confirm: Boolean!) {
    deleteMyAccount(confirm: $confirm) {
      success
      message
    }
  }
`;

export const DELETE_MULTIPLE_CLASSES = gql`
  mutation DeleteMultipleClasses($inputs: [DeleteClassInput!]!) {
    deleteMultipleClasses(inputs: $inputs) {
      success
      message
      deletedCount
    }
  }
`;

export const UPDATE_CLASS_GROUP = gql`
  mutation UpdateClassGroup(
    $className: String!
    $instructorId: String!
    $startTime: String!
    $endTime: String!
    $enrolledStudents: [String!]!
  ) {
    updateClassGroup(
      className: $className
      instructorId: $instructorId
      startTime: $startTime
      endTime: $endTime
      enrolledStudents: $enrolledStudents
    ) {
      success
      message
      updatedCount
    }
  }
`;

export const CREATE_CLASS_CANCELLATION_REQUEST = gql`
  mutation CreateClassCancellationRequest(
    $input: CreateClassCancellationRequestInput!
  ) {
    createClassCancellationRequest(input: $input) {
      success
      message
      data {
        id
        classId
        status
      }
    }
  }
`;

export const APPROVE_CLASS_CANCELLATION_REQUEST = gql`
  mutation ApproveClassCancellationRequest($id: ID!) {
    approveClassCancellationRequest(id: $id) {
      success
      message
      data {
        id
        status
        reviewedByName
        reviewedByRole
        reviewedAt
      }
    }
  }
`;

export const REJECT_CLASS_CANCELLATION_REQUEST = gql`
  mutation RejectClassCancellationRequest($id: ID!) {
    rejectClassCancellationRequest(id: $id) {
      success
      message
      data {
        id
        status
        reviewedByName
        reviewedByRole
        reviewedAt
      }
    }
  }
`;

export const CREATE_PASS = gql`
  mutation CreatePass($input: CreatePassInput!) {
    createPass(input: $input) {
      success
      message
      data {
        id
        passId
        passName
        styleType
      }
    }
  }
`;

export const UPDATE_PASS = gql`
  mutation UpdatePass($id: ID!, $input: UpdatePassInput!) {
    updatePass(id: $id, input: $input) {
      success
      message
      data {
        id
        passId
        passName
        styleType
      }
    }
  }
`;

export const DELETE_PASS = gql`
  mutation DeletePass($id: ID!) {
    deletePass(id: $id) {
      success
      message
    }
  }
`;

export const CREATE_OFFER = gql`
  mutation CreateOffer($input: CreateOfferInput!) {
    createOffer(input: $input) {
      success
      message
      data {
        id
        offerName
        offerCode
        validFrom
        validTill
        discount
        passType
        applicablePasses
        created_at
        updated_at
      }
    }
  }
`;

export const UPDATE_OFFER = gql`
  mutation UpdateOffer($id: ID!, $input: UpdateOfferInput!) {
    updateOffer(id: $id, input: $input) {
      success
      message
      data {
        id
        offerName
        offerCode
        validFrom
        validTill
        discount
        passType
        applicablePasses
        created_at
        updated_at
      }
    }
  }
`;

export const DELETE_OFFER = gql`
  mutation DeleteOffer($id: ID!) {
    deleteOffer(id: $id) {
      success
      message
      data {
        id
      }
    }
  }
`;

export const CREATE_EVENT = gql`
  mutation CreateEvent($input: CreateEventInput!) {
    createEvent(input: $input) {
      success
      message
      data {
        id
        eventName
        location
      }
    }
  }
`;

export const UPDATE_EVENT = gql`
  mutation UpdateEvent($id: ID!, $input: UpdateEventInput!) {
    updateEvent(id: $id, input: $input) {
      success
      message
      data {
        id
        eventName
        location
      }
    }
  }
`;

export const DELETE_EVENT = gql`
  mutation DeleteEvent($id: ID!) {
    deleteEvent(id: $id) {
      success
      message
      data {
        id
      }
    }
  }
`;

export const SEND_NOTIFICATION = gql`
  mutation SendNotification($input: SendNotificationInput!) {
    sendNotification(input: $input) {
      success
      message
    }
  }
`;

export const MARK_NOTIFICATION_AS_READ = gql`
  mutation MarkNotificationAsRead($id: ID!) {
    markNotificationAsRead(id: $id) {
      success
      message
      data {
        id
        isRead
      }
    }
  }
`;

export const BOOK_PASS = gql`
  mutation BookPass($passId: String!, $sessionDate: String) {
    bookPass(passId: $passId, sessionDate: $sessionDate) {
      success
      message
      data {
        id
        passName
        passType
        className
        bookedSessionDate
        bookedSessionDates
        bookedSessions {
          classId
          sessionDate
        }
        purchasedAt
        status
      }
    }
  }
`;

export const BOOK_CLASS_WITH_PASS = gql`
  mutation BookClassWithPass($classId: String!, $sessionDate: String!) {
    bookClassWithPass(classId: $classId, sessionDate: $sessionDate) {
      success
      message
      data {
        id
        passName
        passType
        className
        bookedSessionDate
        bookedSessionDates
        bookedSessions {
          classId
          sessionDate
        }
        purchasedAt
        status
      }
    }
  }
`;


export const CREATE_PASS_PAYMENT_ORDER = gql`
  mutation CreatePassPaymentOrder($passId: String!, $finalPrice: Float!, $returnUrl: String, $promoCode: String, $sessionDate: String) {
    createPassPaymentOrder(passId: $passId, finalPrice: $finalPrice, returnUrl: $returnUrl, promoCode: $promoCode, sessionDate: $sessionDate) {
      success
      message
      paymentSessionId
      orderId
      cfMode
    }
  }
`;

export const VERIFY_PASS_PAYMENT = gql`
  mutation VerifyPassPayment($orderId: String!) {
    verifyPassPayment(orderId: $orderId) {
      success
      message
      isCancelled
      purchaseId
    }
  }
`;


export const BOOK_EVENT = gql`
  mutation BookEvent($eventId: String!) {
    bookEvent(eventId: $eventId) {
      success
      message
      data {
        id
        eventName
        bookedAt
        status
      }
    }
  }
`;

export const CREATE_EVENT_PAYMENT_ORDER = gql`
  mutation CreateEventPaymentOrder($eventId: String!, $finalPrice: Float!, $returnUrl: String, $promoCode: String) {
    createEventPaymentOrder(eventId: $eventId, finalPrice: $finalPrice, returnUrl: $returnUrl, promoCode: $promoCode) {
      success
      message
      paymentSessionId
      orderId
      cfMode
    }
  }
`;

export const VERIFY_EVENT_PAYMENT = gql`
  mutation VerifyEventPayment($orderId: String!) {
    verifyEventPayment(orderId: $orderId) {
      success
      message
      isCancelled
    }
  }
`;

export const CREATE_CART_PAYMENT_ORDER = gql`
  mutation CreateCartPaymentOrder($items: [CartItemInput]!, $returnUrl: String) {
    createCartPaymentOrder(items: $items, returnUrl: $returnUrl) {
      success
      message
      paymentSessionId
      orderId
      cfMode
    }
  }
`;

export const VERIFY_CART_PAYMENT = gql`
  mutation VerifyCartPayment($orderId: String!) {
    verifyCartPayment(orderId: $orderId) {
      success
      message
      isCancelled
    }
  }
`;

export const REFUND_PAYMENT = gql`
  mutation RefundPayment($paymentId: ID!, $reason: String) {
    refundPayment(paymentId: $paymentId, reason: $reason) {
      success
      message
      data {
        id
        status
        refundStatus
        refundId
        cfRefundId
        refundAmount
        refundReason
        refundArn
        refundCreatedAt
        refundedAt
        refundFailureReason
        updated_at
      }
    }
  }
`;

export const SYNC_REFUND_STATUS = gql`
  mutation SyncRefundStatus($paymentId: ID!) {
    syncRefundStatus(paymentId: $paymentId) {
      success
      message
      data {
        id
        status
        refundStatus
        refundId
        cfRefundId
        refundAmount
        refundReason
        refundArn
        refundCreatedAt
        refundedAt
        refundFailureReason
        updated_at
      }
    }
  }
`;

export const MARK_ALL_AS_READ = gql`
  mutation MarkAllAsRead {
    markAllAsRead {
      success
      message
    }
  }
`;

export const REFRESH_TOKEN = gql`
  mutation RefreshToken {
    refreshToken {
      success
      message
      token
      user {
        id
        email
        fullname
        role
      }
    }
  }
`;
